// src\app\app.module.ts
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { NavigationComponent } from './components/navigation/navigation.component';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { ApplicationsPageComponent } from './page/applications-page/applications-page.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { HeaderComponent } from './components/header/header.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { NumbersOnlyDirective } from './directives/numbers-only/numbers-only.directive';
import { PasswordViewerDirective } from './directives/password-viewer/password-viewer.directive';
import { FormComponent } from './components/form/form.component';
import { LoginPageComponent } from './page/login-page/login-page.component';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PhonePipe } from './pipes/phone/phone.pipe';
import { TableComponent } from './components/table/table.component';
import { CustomerPageComponent } from './page/customer-page/customer-page.component';
import { AppRoutesModule } from './routes.module';
import { AuthInterceptor } from './interceptors/auth/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error/error.interceptor';
import { InvitePageComponent } from './page/invite-page/invite-page.component';
import { HomePageComponent } from './page/home-page/home-page.component';
import { StatusPipe } from './pipes/status/status.pipe';
import { TableActionsComponent } from './components/table-actions/table-actions.component';
import { SignupPageComponent } from './page/signup-page/signup-page.component';
import { ApplicationsAndInvitesPageComponent } from './page/applications-and-invites-page/applications-and-invites-page.component';
import { NewAppDialogComponent } from './dialogs/new-app-dialog/new-app-dialog.component';
import { SettingsModalComponent } from './components/settings-modal/settings-modal.component';
import { PasswordToggleButtonComponent } from './components/password-toggle-button/password-toggle-button.component';
import { ForgotPasswordPageComponent } from './page/forgot-password-page/forgot-password-page.component';
import { ChangePasswordPageComponent } from './page/change-password-page/change-password-page.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ExtendSessionDialogComponent } from './dialogs/extend-session-dialog/extend-session-dialog.component';
import { TableHeaderActionsComponent } from './components/table-header-actions/table-header-actions.component';
import { ResizableDirective } from './directives/resizable/resizable.directive';
import { SanitizedHtmlDirective } from './directives/sanitized-html/sanitized-html.directive';
import { MaxValidatorDirective } from './shared/min-max-validator';
import { MinValidatorDirective } from './shared/min-validator';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TosPageComponent } from './page/tos-page/tos-page.component';
import { TosContentComponent } from './components/tos-content/tos-content.component';
import { AutopayConfirmationDialogComponent } from './dialogs/autopay-confirmation-dialog/autopay-confirmation-dialog.component';
import { FrontendRouteInterceptor } from './interceptors/frontend-route/frontend-route.interceptor';
import { NativeDateAdapter } from '@angular/material/core';
import { PeoplePageComponent } from './page/people-page/people-page.component';
import { UserManagementDialogComponent } from './dialogs/user-management-dialog/user-management-dialog.component';
import { EditGroupDialogComponent } from './dialogs/edit-group-dialog/edit-group-dialog.component';
import { InviteUserDialogComponent } from './dialogs/invite-user-dialog/invite-user-dialog.component';
import { MakePaymentDialogComponent } from './dialogs/make-payment-dialog/make-payment-dialog.component';
import { SuspensionDialogComponent } from './dialogs/suspension-dialog/suspension-dialog.component';
import { CancelRequestDialogComponent } from './dialogs/cancel-request-dialog/cancel-request-dialog.component';
import { SupplierAuthorizationsTableComponent } from './tables/supplier-authorizations-table/supplier-authorizations-table.component';
import { SupplierPastTransactionsTableComponent } from './tables/supplier-past-transactions-table/supplier-past-transactions-table.component';
import { SupplierUpcomingTransactionsTableComponent } from './tables/supplier-upcoming-transactions-table/supplier-upcoming-transactions-table.component';
import { BorrowerAuthorizationsTableComponent } from './tables/borrower-authorizations-table/borrower-authorizations-table.component';
import { BorrowerPastTransactionsTableComponent } from './tables/borrower-past-transactions-table/borrower-past-transactions-table.component';
import { BorrowerUpcomingTransactionsTableComponent } from './tables/borrower-upcoming-transactions-table/borrower-upcoming-transactions-table.component';
import { SupplierTransactionPageComponent } from './page/supplier-transaction-page/supplier-transaction-page.component';
import { BorrowerTransactionPageComponent } from './page/borrower-transaction-page/borrower-transaction-page.component';
import { CaptureAuthorizationDialogComponent } from './dialogs/capture-authorization-dialog/capture-authorization-dialog.component';
import { BuildOfferDialogComponent } from './dialogs/build-offer-dialog/build-offer-dialog.component';
import { VoidAuthorizationDialogComponent } from './dialogs/void-authorization-dialog/void-authorization-dialog.component';
import { RefundAuthorizationDialogComponent } from './dialogs/refund-authorization-dialog/refund-authorization-dialog.component';
import { ReviewOfferDialogComponent } from './dialogs/review-offer-dialog/review-offer-dialog.component';
import { BorrowerTransactionRequestsTableComponent } from './tables/borrower-transaction-requests-table/borrower-transaction-requests-table.component';
import { BorrowerRequestPageComponent } from './page/borrower-request-page/borrower-request-page.component';
import { TransactionDetailsComponent } from './components/transaction-details/transaction-details.component';
import { TransactionDetailsTableComponent } from './tables/transaction-details-table/transaction-details-table.component';
import { SupplierTransactionRequestsTableComponent } from './tables/supplier-transaction-requests-table/supplier-transaction-requests-table.component';
import { BorrowerKpiComponent } from './components/borrower-kpi/borrower-kpi.component';
import { TransactionAuthorizationDialogComponent } from './dialogs/transaction-authorization-dialog/transaction-authorization-dialog.component';
import { TransactionRequestOfferDialogComponent } from './dialogs/transaction-request-offer-dialog/transaction-request-offer-dialog.component';
import { EditPaymentDialogComponent } from './dialogs/edit-payment-dialog/edit-payment-dialog.component';
import { TosViewerDialogComponent } from './dialogs/tos-viewer-dialog/tos-viewer-dialog.component';
import { RefundLoanDialogComponent } from './dialogs/refund-loan-dialog/refund-loan-dialog.component';
import { IsYouPipe } from './pipes/is-you/is-you.pipe';
import { TosContentHistoricalComponent } from './components/tos-content-historical/tos-content-historical.component';
import { ReviewRequestDialogComponent } from './dialogs/review-request-dialog/review-request-dialog.component';
import { SubmitRequestDialogComponent } from './dialogs/submit-request-dialog/submit-request-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    ApplicationsPageComponent,
    ApplicationsAndInvitesPageComponent,
    NavigationComponent,
    HeaderComponent,
    NumbersOnlyDirective,
    FormComponent,
    TableComponent,
    LoginPageComponent,
    ForgotPasswordPageComponent,
    CustomerPageComponent,
    InvitePageComponent,
    TableActionsComponent,
    NewAppDialogComponent,
    SignupPageComponent,
    HomePageComponent,
    ChangePasswordPageComponent,
    ExtendSessionDialogComponent,
    TableHeaderActionsComponent,
    MaxValidatorDirective,
    MinValidatorDirective,
    ConfirmationDialogComponent,
    TosPageComponent,
    TosContentComponent,
    TosContentHistoricalComponent,
    AutopayConfirmationDialogComponent,
    PeoplePageComponent,
    UserManagementDialogComponent,
    ConfirmationDialogComponent,
    EditGroupDialogComponent,
    InviteUserDialogComponent,
    MakePaymentDialogComponent,
    SuspensionDialogComponent,
    CancelRequestDialogComponent,
    SupplierTransactionPageComponent,
    BorrowerTransactionPageComponent,
    SupplierAuthorizationsTableComponent,
    SupplierPastTransactionsTableComponent,
    SupplierUpcomingTransactionsTableComponent,
    BorrowerAuthorizationsTableComponent,
    BorrowerPastTransactionsTableComponent,
    BorrowerUpcomingTransactionsTableComponent,
    CaptureAuthorizationDialogComponent,
    BuildOfferDialogComponent,
    VoidAuthorizationDialogComponent,
    RefundAuthorizationDialogComponent,
    ReviewOfferDialogComponent,
    BorrowerTransactionRequestsTableComponent,
    BorrowerRequestPageComponent,
    TransactionDetailsComponent,
    TransactionDetailsTableComponent,
    SupplierTransactionRequestsTableComponent,
    BorrowerKpiComponent,
    TransactionAuthorizationDialogComponent,
    TransactionRequestOfferDialogComponent,
    EditPaymentDialogComponent,
    TosViewerDialogComponent,
    RefundLoanDialogComponent,
    ReviewRequestDialogComponent,
    SubmitRequestDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    SanitizedHtmlDirective,
    BrowserModule,
    FormsModule,
    CommonModule,
    AgGridModule,
    AppRoutesModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NgxSmartModalModule.forRoot(),
    ToastrModule.forRoot(),
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    MatSidenavModule,
    MatMenuModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PhonePipe,
    StatusPipe,
    IsYouPipe,
    SettingsModalComponent,
    PasswordToggleButtonComponent,
    PasswordViewerDirective,
    MatTooltipModule,
    ResizableDirective,
  ],
  providers: [
    CurrencyPipe,
    NativeDateAdapter,
    provideNgxMask(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FrontendRouteInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
